import React, {
  useCallback,
  useContext,
  useMemo,
  useReducer,
  Dispatch,
  createContext,
} from 'react';
import { useHistory } from 'react-router-dom';
import { PlaidLinkError } from 'react-plaid-link';

import { getLinkToken } from './api';
import {
  AssetType,
  ItemType,
  TransactionsType,
  AccountsType,
} from '../components/types';

interface AccountState {
  [itemId: number]: AccountsType;
}

interface ErrorsState {
  code?: string;
  message?: string;
}

interface Users {
  [key: string]: UserState;
}

interface UserState {
  linkError: PlaidLinkError;
  linkToken: string | null;
  transactions: TransactionsType | [];
  items: Array<ItemType> | [];
  assets: AssetType[] | [];
  accounts: AccountState;
  errors: ErrorsState;
}

const initialState = {};

type AccountAction =
  | {
      type: 'LINK_TOKEN_CREATED';
      username: string;
      token: string;
    }
  | { type: 'LINK_TOKEN_ERROR'; error: PlaidLinkError; username: string }
  | { type: 'DELETE_LINK_TOKEN'; username: string }
  | { type: 'CREATE_USER'; username: string }
  | {
      type: 'ADD_TRANSACTIONS';
      username: string;
      transactions: TransactionsType;
    }
  | {
      type: 'ADD_ACCOUNTS';
      username: string;
      accounts: AccountsType;
      itemId: string;
    }
  | { type: 'ADD_ASSET'; asset: AssetType; username: string }
  | {
      type: 'DELETE_ASSET';
      description: string;
      value: number;
      username: string;
    }
  | {
      type: 'SET_ERROR';
      errors: ErrorsState;
      username: string;
    }
  | {
      type: 'RESET_ERROR';
      errors: ErrorsState;
      username: string;
    }
  | {
      type: 'CREATE_ITEM';
      username: string;
      itemId: string;
      institutionId: string;
    }
  | { type: 'DELETE_ITEM'; itemId: string; username: string };

interface UsersContextShape extends UserState {
  dispatch: Dispatch<AccountAction>;
  generateLinkToken: (
    username: string,
    itemId: number | null | undefined
  ) => void;
  createItem: (
    username: string,
    itemId: string,
    institutionId: string | null | undefined
  ) => void;
  deleteItem: (itemId: string, username: string) => void;
  deleteLinkToken: (username: string) => void;
  users: Users;
  addAsset: (asset: AssetType, username: string) => void;
  deleteAsset: (description: string, value: number, username: string) => void;
  addTransactionsData: (
    username: string,
    transactions: TransactionsType,
    accounts: AccountsType,
    item_id: string
  ) => void;
  setUsername: (username: string) => void;
  setError: (code: string, message: string | null, username: string) => void;
  resetError: (username: string) => void;
}

const UsersContext = createContext<UsersContextShape>(
  initialState as UsersContextShape
);

export function UsersProvider(props: any) {
  const [users, dispatch] = useReducer(reducer, initialState);

  const history = useHistory();

  const setUsername = useCallback(
    async username => {
      console.log(username);
      dispatch({
        type: 'CREATE_USER',
        username: username,
      });
      history.push(`/user/${username}`);
    },
    [history]
  );

  const generateLinkToken = useCallback(async username => {
    const linkTokenResponse = await getLinkToken();
    if (linkTokenResponse.data.link_token) {
      const token = await linkTokenResponse.data.link_token;
      console.log('success', linkTokenResponse.data);
      dispatch({
        type: 'LINK_TOKEN_CREATED',
        username: username,
        token: token,
      });
    } else {
      dispatch({
        type: 'LINK_TOKEN_ERROR',
        error: linkTokenResponse.data,
        username: username,
      });
      console.log('error', linkTokenResponse.data);
    }
  }, []);

  const createItem = useCallback(async (username, itemId, institutionId) => {
    dispatch({
      type: 'CREATE_ITEM',
      username: username,
      itemId: itemId,
      institutionId: institutionId,
    });
  }, []);

  const deleteItem = useCallback(async (itemId, username) => {
    dispatch({
      type: 'DELETE_ITEM',
      itemId: itemId,
      username: username,
    });
  }, []);

  // called in link onSuccess callback
  const addTransactionsData = useCallback(
    async (
      username: string,
      transactions: TransactionsType,
      accounts: AccountsType,
      item_id: string
    ) => {
      dispatch({
        type: 'ADD_TRANSACTIONS',
        username: username,
        transactions: transactions,
      });

      dispatch({
        type: 'ADD_ACCOUNTS',
        username: username,
        accounts: accounts,
        itemId: item_id,
      });
    },
    []
  );

  const addAsset = useCallback(async (asset, username) => {
    dispatch({
      type: 'ADD_ASSET',
      asset: asset,
      username: username,
    });
  }, []);

  const deleteAsset = useCallback(async (description, value, username) => {
    dispatch({
      type: 'DELETE_ASSET',
      description: description,
      value: value,
      username: username,
    });
  }, []);

  // called in link onSuccess callback
  const deleteLinkToken = useCallback(async username => {
    dispatch({
      type: 'DELETE_LINK_TOKEN',
      username: username,
    });
  }, []);

  // called in link onExit callback
  const setError = useCallback(
    async (code: string, message: string, username: string) => {
      dispatch({
        type: 'SET_ERROR',
        errors: { code: code, message: message },
        username: username,
      });
    },
    []
  );

  const resetError = useCallback(async (username: string) => {
    dispatch({
      type: 'RESET_ERROR',
      errors: {},
      username: username,
    });
  }, []);

  const value = useMemo(
    () => ({
      generateLinkToken,
      deleteLinkToken,
      setUsername,
      createItem,
      deleteItem,
      addTransactionsData,
      addAsset,
      deleteAsset,
      setError,
      resetError,
      users,
    }),
    [
      users,
      generateLinkToken,
      createItem,
      deleteItem,
      deleteLinkToken,
      addTransactionsData,
      addAsset,
      deleteAsset,
      setUsername,
      setError,
      resetError,
    ]
  );

  return <UsersContext.Provider value={value} {...props} />;
}

/**
 * @desc Handles updates to the Users state as dictated by dispatched actions.
 */
function reducer(state: any, action: AccountAction) {
  let name;
  switch (action.type) {
    case 'CREATE_USER':
      return {
        ...state,
        [action.username]: {
          linkToken: '',
          transactions: [],
          accounts: {},
          items: [],
          assets: [],
          linkError: {},
          errors: {},
        },
      };
    case 'LINK_TOKEN_CREATED':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          linkToken: action.token,
          errors: {},
        },
      };
    case 'LINK_TOKEN_ERROR':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          linkError: action.error,
        },
      };
    case 'CREATE_ITEM':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          items: [
            ...state[name].items,
            {
              id: action.itemId,
              plaid_institution_id: action.institutionId,
              status: 'good',
            },
          ],
        },
      };
    case 'DELETE_ITEM':
      name = action.username;
      const accountIds = [...state[name].accounts[action.itemId]].map(
        account => account.account_id
      );
      const accounts = { ...state[name].accounts };
      delete accounts[action.itemId];
      return {
        ...state,
        [name]: {
          ...state[name],
          items: [...state[name].items].filter(item => {
            return item.id !== action.itemId;
          }),
          transactions: [...state[name].transactions].filter(
            tx => !accountIds.includes(tx.account_id)
          ),
          accounts: accounts,
        },
      };
    case 'ADD_TRANSACTIONS':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          transactions: [...state[name].transactions, ...action.transactions],
        },
      };
    case 'ADD_ACCOUNTS':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          accounts: {
            ...state[name].accounts,
            [action.itemId]: action.accounts,
          },
        },
      };
    case 'ADD_ASSET':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          assets: [...state[name].assets, action.asset],
        },
      };
    case 'DELETE_ASSET':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          assets: [...state[name].assets].filter(asset => {
            return !(
              asset.description === action.description &&
              asset.value === action.value
            );
          }),
        },
      };
    case 'DELETE_LINK_TOKEN':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          linkToken: '',
        },
      };
    case 'SET_ERROR':
      name = action.username;
      if (action.errors == null) {
        return state;
      }
      return {
        ...state,
        [name]: {
          ...state[name],
          errors: {
            code: action.errors.code,
            message: action.errors.message,
          },
        },
      };
    case 'RESET_ERROR':
      name = action.username;
      return {
        ...state,
        [name]: {
          ...state[name],
          errors: {},
        },
      };
    default:
      console.warn('unknown action');
      return state;
  }
}

/**
 * @desc A convenience hook to provide access to the Link context state in components.
 */
export default function useUsers() {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error(`useUsers must be used within a UsersProvider`);
  }

  return context;
}
